import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Mail, MessageSquare, Phone } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UnsubscribeModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    unsubscribe_email: true,
    unsubscribe_sms: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email && !formData.phone) {
      toast.error('Please enter your email address or phone number');
      return;
    }

    if (!formData.unsubscribe_email && !formData.unsubscribe_sms) {
      toast.error('Please select at least one option to unsubscribe');
      return;
    }

    if (formData.unsubscribe_sms && !formData.phone) {
      toast.error('Please enter your phone number to unsubscribe from SMS');
      return;
    }

    if (formData.unsubscribe_email && !formData.email) {
      toast.error('Please enter your email to unsubscribe from email communications');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/unsubscribe`, formData);
      toast.success('You have been successfully unsubscribed');
      setFormData({ email: '', phone: '', unsubscribe_email: true, unsubscribe_sms: false });
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to unsubscribe. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border border-gray-200 shadow-2xl max-w-md" data-testid="unsubscribe-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Unsubscribe
          </DialogTitle>
        </DialogHeader>

        <p className="text-gray-500 text-sm mt-2">
          We're sorry to see you go. Please enter your details and select which communications you'd like to unsubscribe from.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <div>
            <Label htmlFor="unsub-email" className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">
              Email Address {formData.unsubscribe_email && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id="unsub-email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              className="bg-white border-gray-300 focus:border-[#1D4ED8] text-gray-900"
              placeholder="you@example.com"
              data-testid="unsubscribe-email-input"
            />
          </div>

          <div>
            <Label htmlFor="unsub-phone" className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">
              Phone Number {formData.unsubscribe_sms && <span className="text-red-500">*</span>}
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
              <Input
                id="unsub-phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                className="pl-10 bg-white border-gray-300 focus:border-[#1D4ED8] text-gray-900"
                placeholder="+1 555 123 4567"
                data-testid="unsubscribe-phone-input"
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-wider text-gray-500 block">
              Unsubscribe From
            </Label>
            
            <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-sm border border-gray-200">
              <Checkbox
                id="unsubscribe_email"
                checked={formData.unsubscribe_email}
                onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, unsubscribe_email: checked }))}
                className="mt-0.5 border-gray-300 data-[state=checked]:bg-[#1D4ED8] data-[state=checked]:border-[#1D4ED8]"
                data-testid="unsubscribe-email-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="unsubscribe_email" className="text-sm text-gray-900 cursor-pointer flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-[#1D4ED8]" strokeWidth={1.5} />
                  Email Communications
                </Label>
                <p className="text-xs text-gray-400 mt-1">
                  Newsletter, promotional emails, and updates
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-sm border border-gray-200">
              <Checkbox
                id="unsubscribe_sms"
                checked={formData.unsubscribe_sms}
                onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, unsubscribe_sms: checked }))}
                className="mt-0.5 border-gray-300 data-[state=checked]:bg-[#1D4ED8] data-[state=checked]:border-[#1D4ED8]"
                data-testid="unsubscribe-sms-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="unsubscribe_sms" className="text-sm text-gray-900 cursor-pointer flex items-center">
                  <MessageSquare className="w-4 h-4 mr-2 text-[#1D4ED8]" strokeWidth={1.5} />
                  SMS Communications
                </Label>
                <p className="text-xs text-gray-400 mt-1">
                  Text messages and SMS alerts
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-sm p-3">
            <p className="text-[10px] text-gray-500 leading-relaxed">
              By submitting this form, you will be removed from our selected mailing lists. 
              Please allow up to 48 hours for your request to be processed. You may still receive 
              transactional emails related to your account. You can also text STOP to any SMS message to unsubscribe immediately.
            </p>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#1D4ED8] text-white hover:bg-[#1E3A8A] font-semibold uppercase tracking-wide py-2 rounded-sm"
            data-testid="unsubscribe-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Unsubscribe'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
